<script setup lang="ts">
import Button from "primevue/button";
import { computed, nextTick, onBeforeUnmount, onMounted, Ref, ref, watch } from "vue";
import {
    DirectLink,
    Participant,
    Product,
    ProductLanguages,
    Project,
    ProjectRole,
    Trainer,
    TranslatedProduct
} from "../../types/PersologOnlineTypes.ts";
import { FilterMatchMode } from "@primevue/core/api";
import { useI18n } from "vue-i18n";
import { ProfileAdministrationAPI } from "../../api";
import ProfileViewButton from "../common/ProfileViewButton.vue";
import QuestionnaireSendButton from "./activation/QuestionnaireSendButton.vue";
import AddParticipants from "./AddParticipants.vue";
import ProjectDetailsParticipantDetails from "./ProjectDetailsParticipantDetails.vue";
import ProfileZipDownload from "../profiles/ProfileZipDownload.vue";
import SendOrRemindAll from "./activation/SendOrRemindAll.vue";
import ProfileReleaseButton from "../common/ProfileReleaseButton.vue";
import {
    fetchRoles,
    getProduct,
    getProductNames,
    getProductQuantity,
    productHasBenchmarkDiagram
} from "../../utils/ProjectUtils.ts";
import BuyButton from "../common/BuyButton.vue";
import { useProjectDepartmentsStore } from "../../stores/projectDepartments";
import ParticipantActionsButton from "./participant-actions/ParticipantActionsButton.vue";
import ParticipantEmailButton from "./participant-actions/ParticipantEmailButton.vue";
import EmailAllButton from "./participant-actions/EmailAllButton.vue";
import { copyToClipboard, isMobileView, isValidEmail } from "../../utils/GeneralUtils";
import { toPercentage } from "chart.js/helpers";
import { fetchData } from "../../utils/ProductViewUtils.ts";
import { useToast } from "primevue/usetoast";
import { useFitZoom } from "../../composables/useFitZoom";

const { t, locale } = useI18n();
const toast = useToast();

const props = defineProps({
    product: {
        type: String,
        required: true
    },
    project: {
        type: Object as () => Project,
        required: true
    },
    participants: {
        type: Array as () => Participant[],
        required: true
    },
    trainer: {
        type: Object as () => Trainer,
        required: true
    },
    languages: {
        type: Object as () => ProductLanguages | null,
        default: null
    },
    canAddParticipants: {
        type: Boolean,
        default: true
    },
    directLink: {
        type: Object as () => DirectLink | null,
        default: null
    }
});

const emit = defineEmits(["update:participants", "reload:participants", "update:project"]);

const dt = ref();
const exportCSV = () => {
    dt.value.exportCSV();
};

// Show the stacked card list (instead of the wide table) up to PrimeFlex's `lg` breakpoint,
// so tablets/iPads get the readable layout rather than a horizontally scrolling table.
const CARD_VIEW_BREAKPOINT = 991;
const useCardView = () => isMobileView(CARD_VIEW_BREAKPOINT);
// Re-evaluated on resize, so rotating a tablet or resizing the window switches the layout (RDT-456).
const isNarrowWindow = ref(useCardView());
const updateCardView = () => {
    isNarrowWindow.value = useCardView();
};
onMounted(() => globalThis.addEventListener("resize", updateCardView));
onBeforeUnmount(() => globalThis.removeEventListener("resize", updateCardView));

// A table that does not fit the dialog even at its narrowest shrinks as a whole, down to 80%; below that
// the cards take over, since they offer the same (RDT-456).
const MIN_TABLE_ZOOM = 0.8;
const widthProbe = ref<HTMLElement | null>(null);

/** The narrowest the table can get: every cell wrapped as far as its content allows. */
function measureTableWidth(tableRoot: HTMLElement) {
    const table = tableRoot.querySelector<HTMLElement>(".p-datatable-table");
    if (!table) {
        return 0;
    }
    const width = table.style.width;
    table.style.width = "min-content";
    const needed = table.offsetWidth;
    table.style.width = width;
    return needed;
}

// Switching to the cards in the middle of an edit would throw the trainer out of it: while a row or a
// grouping is being edited, the table stays and shrinks no further than the minimum zoom.
const { tooWide: tableTooWide, scheduleFit: fitTable } = useFitZoom(
    widthProbe,
    () => dt.value?.$el,
    measureTableWidth,
    MIN_TABLE_ZOOM,
    () => isEditingTable.value
);
const isCardView = computed(() => isNarrowWindow.value || tableTooWide.value);

// Only the global search: the per-column filter menus were dropped to give the table room (RDT-456).
const filters = ref<{
    global: { value: string | null; matchMode: typeof FilterMatchMode.CONTAINS };
}>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS }
});
const productOptions = ref<{ label: string; value: string }[]>([]);
const products = ref<TranslatedProduct[]>([]);
const expandedRows: Ref<Record<number, boolean>> = ref({});
const expandedRowsMobile: Ref<Record<number, boolean>> = ref({});
const rows = ref(10);
const showPaginator = computed(() => props.participants.length > rows.value);

const purchasedProduct = ref<Product>();
const quantity = ref<number>(0);
const roles = ref<ProjectRole[]>([]);
const departmentsStore = useProjectDepartmentsStore();
const departments = computed(() => departmentsStore.getForProject(props.project.id ?? 0));
const departmentOptions = computed(() => [
    { id: null, name: t("buttons.remove") },
    ...departments.value.map((department) => ({ id: department.id ?? null, name: department.name }))
]);
const roleOptions = computed(() => [
    { id: null, name: t("buttons.remove") },
    ...roles.value.map((role) => ({ id: role.id ?? null, name: role.name }))
]);
const productNames = ref<Record<string, string>>({});

const hideDeleted = ref(true);
const hasDeletedParticipants = computed(() => props.participants.some((p) => p.status === "DELETED"));
const filteredParticipants = ref(props.participants);

/**
 * A participant enriched with the product/quantity context needed to render the contextual
 * action buttons (buy, send, view, ...) both in the desktop table and the mobile list.
 */
type EnrichedParticipant = Participant & {
    product?: string;
    productName?: string;
    purchasedProduct?: Product;
    quantity?: number;
    roleName?: string;
    departmentName?: string;
};

/**
 * Enriches the participants with additional information like role name and department name.
 * These are the participants that are displayed in the table after filtering.
 */
const enrichedParticipants = ref<EnrichedParticipant[]>([]);

// True until the first enrichment pass completes, so the table shows a loading overlay instead of
// briefly flashing the "No participants yet" empty state while the async enrichment is running.
const loading = ref(true);

const loadEnrichedParticipants = async () => {
    try {
        enrichedParticipants.value = await Promise.all(
            filteredParticipants.value.map(async (participant) => {
                const selectedProduct = participant.selectedProduct || props.project?.defaultProduct || props.project?.product;
                const product =
                    props.project && props.project?.product === props.project?.defaultProduct
                        ? purchasedProduct.value
                        : await getProduct(props.trainer?.id, selectedProduct);

                const profileQuantity =
                    props.project && props.project?.product === props.project?.defaultProduct
                        ? quantity.value
                        : await getProductQuantity(props.trainer?.id, selectedProduct);

                const productName = productNames.value?.[selectedProduct] ?? selectedProduct;

                return {
                    ...participant,
                    selectedProduct,
                    product: selectedProduct,
                    productName,
                    purchasedProduct: product,
                    quantity: profileQuantity,
                    roleName: participant.roleId ? getRole(participant.roleId) || "" : "",
                    departmentName: participant.departmentId ? getDepartment(participant.departmentId) || "" : ""
                };
            })
        );
    } finally {
        loading.value = false;
    }
};

async function fetchProducts() {
    const allProducts = await fetchData();
    products.value = allProducts.all.map(
        (product) => allProducts.purchased.find((p) => p.productNo === product.productNo) ?? product
    );
}

function showInfo(value: boolean, id: number) {
    expandedRows.value = { [id]: value };
    expandedRowsMobile.value[id] = value;
}

function toggleCollapse(participantId: number) {
    expandedRowsMobile.value[participantId] = !expandedRowsMobile.value[participantId];
}

function resetSearch() {
    filters.value.global.value = "";
    filterParticipants();
}

/**
 * Filter the participants based on the deleted status and load the enriched participants.
 * The search is applied on top of the enriched list: by the DataTable's global filter in the table,
 * by `cardParticipants` in the card list, so typing never re-runs the enrichment requests.
 * While an editor is open this waits for it to close, see `isEditing`.
 */
function filterParticipants() {
    if (isEditing.value) {
        reloadPending = true;
        return;
    }
    filteredParticipants.value = hideDeleted.value
        ? props.participants.filter((p) => p.status !== "DELETED")
        : props.participants;
    loadEnrichedParticipants();
}

// ---- Card list: search, sorting and the inline editor (RDT-456) ----
// The cards have no column headers, filter menus or row editor, so these replace them there.

type CardSortField = "firstName" | "name" | "score" | "email" | "status" | "roleName" | "departmentName";

// Lifecycle order instead of the alphabetical status codes, so "status" sorts by progress.
const STATUS_ORDER = [
    "CREATED",
    "ACTIVATED",
    "STARTED",
    "COMPLETED",
    "CONVERTED",
    "FILLED",
    "CALCULATED",
    "GENERATED",
    "NOTIFIED",
    "REVOKED",
    "ARCHIVED",
    "DELETED",
];

const isBenchmarkProject = computed(() => productHasBenchmarkDiagram(props.project.product));
const hasProductColumn = computed(
    () => !!props.project.defaultProduct && props.project.product !== props.project.defaultProduct
);

// Same default order as the table: ranking projects by score, the rest by last name.
const defaultSortOrder = (field: CardSortField) => (field === "score" ? "desc" : "asc");
const cardSortField = ref<CardSortField>(isBenchmarkProject.value ? "score" : "name");
const cardSortOrder = ref<"asc" | "desc">(defaultSortOrder(cardSortField.value));
watch(cardSortField, (field) => {
    cardSortOrder.value = defaultSortOrder(field);
});

/** The fields the table can sort by, in its column order. */
const cardSortOptions = computed(() => {
    const columns = "project-details.participants.columns";
    const options: { value: CardSortField; label: string }[] = [];
    if (!props.project.anonymous) {
        options.push({ value: "firstName", label: t(`${columns}.firstName`) });
    }
    options.push({ value: "name", label: t(`${columns}.lastName`) });
    if (isBenchmarkProject.value) {
        options.push({ value: "score", label: t(`${columns}.score`) });
    }
    if (!props.project.anonymous) {
        options.push({ value: "email", label: t(`${columns}.email`) });
    }
    options.push({ value: "status", label: t(`${columns}.status`) });
    if (roles.value.length > 0) {
        options.push({ value: "roleName", label: t(`${columns}.role`) });
    }
    if (departments.value.length > 0) {
        options.push({ value: "departmentName", label: t(`${columns}.department`) });
    }
    return options;
});

function toggleCardSortOrder() {
    cardSortOrder.value = cardSortOrder.value === "asc" ? "desc" : "asc";
}

function cardSortValue(participant: EnrichedParticipant, field: CardSortField): string | number | undefined {
    if (field === "score") {
        return participant.score ?? undefined;
    }
    if (field === "status") {
        const index = STATUS_ORDER.indexOf(participant.status ?? "");
        return index === -1 ? STATUS_ORDER.length : index;
    }
    return participant[field] || undefined;
}

function compareCards(a: EnrichedParticipant, b: EnrichedParticipant, collator: Intl.Collator) {
    const first = cardSortValue(a, cardSortField.value);
    const second = cardSortValue(b, cardSortField.value);
    // Participants without a value (no score yet, no role, ...) stay at the end in both directions.
    if (first === undefined || second === undefined) {
        return Number(first === undefined) - Number(second === undefined);
    }
    const result =
        typeof first === "number" && typeof second === "number"
            ? first - second
            : collator.compare(String(first), String(second));
    return cardSortOrder.value === "desc" ? -result : result;
}

function matchesCardSearch(participant: EnrichedParticipant, query: string) {
    return [
        participant.firstName,
        participant.name,
        `${participant.firstName} ${participant.name}`,
        `${participant.name} ${participant.firstName}`,
        participant.email,
        t(`status.${participant.status}`),
        participant.roleName,
        participant.departmentName,
    ].some((value) => value?.toLowerCase().includes(query));
}

/** What the card list shows: the enriched participants, searched as the trainer types, then sorted. */
const cardParticipants = computed(() => {
    const query = filters.value.global.value?.trim().toLowerCase() ?? "";
    const matching = query
        ? enrichedParticipants.value.filter((participant) => matchesCardSearch(participant, query))
        : enrichedParticipants.value;
    const collator = new Intl.Collator(locale.value, { sensitivity: "base", numeric: true });
    return [...matching].sort((a, b) => compareCards(a, b, collator));
});

/** What the card editor and the row editor let the trainer change. */
type ParticipantDraft = {
    firstName: string;
    name: string;
    email: string;
    selectedProduct: string;
    roleId: number | null;
    departmentId: number | null;
};

function draftOf(participant: Participant): ParticipantDraft {
    return {
        firstName: participant.firstName,
        name: participant.name,
        email: participant.email,
        selectedProduct: participant.selectedProduct,
        // The backend reports "none" as 0.
        roleId: participant.roleId || null,
        departmentId: participant.departmentId || null,
    };
}

function isDraftUnchanged(draft: ParticipantDraft, opened: ParticipantDraft) {
    return (Object.keys(draft) as (keyof ParticipantDraft)[]).every((field) => draft[field] === opened[field]);
}

/**
 * The participant as stored, not the enriched copy with its display-only fields, with the changes from the draft
 * that the participant's status allows. The caller checks the e-mail address first.
 */
function applyDraft(participant: EnrichedParticipant, draft: ParticipantDraft): Participant {
    const updated: Participant = { ...(props.participants.find((p) => p.id === participant.id) ?? participant) };
    if (isEditable(participant.status)) {
        updated.firstName = draft.firstName;
        updated.name = draft.name;
        updated.email = draft.email;
        if (hasProductColumn.value && isProductEditable(participant)) {
            updated.selectedProduct = draft.selectedProduct;
        }
    }
    if (isGroupingEditable(participant.status)) {
        // Omitted, the backend stores none.
        if (roles.value.length > 0) {
            updated.roleId = draft.roleId ?? undefined;
        }
        if (departments.value.length > 0) {
            updated.departmentId = draft.departmentId ?? undefined;
        }
    }
    return updated;
}

const editingCardId = ref<number | null>(null);
const cardDraft = ref<ParticipantDraft | null>(null);
// What the editor opened with, to skip the request when nothing was changed.
let cardDraftOpened: ParticipantDraft | null = null;
const cardEmailInvalid = ref(false);
const savingCard = ref(false);

/**
 * Whether the card offers its editor. The rules are the table's: name, e-mail and product while the
 * questionnaire is open, role and department also once the profile is generated. Anonymous projects
 * have no edit column in the table either.
 */
function canEditCard(participant: Participant) {
    if (props.project.anonymous) {
        return false;
    }
    const hasGroupings = roles.value.length > 0 || departments.value.length > 0;
    return isEditable(participant.status) || (hasGroupings && isGroupingEditable(participant.status));
}

function isProductEditable(participant: Participant) {
    return isEditable(participant.status) && participant.status !== "ACTIVATED";
}

function openCardEditor(participant: EnrichedParticipant) {
    if (!participant.id || !canEditCard(participant)) {
        return;
    }
    editingCardId.value = participant.id;
    cardEmailInvalid.value = false;
    cardDraft.value = draftOf(participant);
    cardDraftOpened = { ...cardDraft.value };
}

function closeCardEditor() {
    editingCardId.value = null;
    cardDraft.value = null;
    cardDraftOpened = null;
    cardEmailInvalid.value = false;
}

async function saveCardEditor(participant: EnrichedParticipant) {
    const draft = cardDraft.value;
    const opened = cardDraftOpened;
    if (!draft || !opened || !participant.id) {
        closeCardEditor();
        return;
    }
    if (isDraftUnchanged(draft, opened)) {
        closeCardEditor();
        return;
    }
    if (isEditable(participant.status) && !isValidEmail(draft.email)) {
        cardEmailInvalid.value = true;
        return;
    }
    const updated = applyDraft(participant, draft);

    savingCard.value = true;
    try {
        await ProfileAdministrationAPI.updateParticipant(updated);
        emit(
            "update:participants",
            props.participants.map((p) => (p.id === updated.id ? updated : p))
        );
        closeCardEditor();
        filterParticipants();
    } catch (error) {
        console.error(error);
        toast.add({
            severity: "error",
            detail: t("errors.unexpectedError"),
            life: 3000,
        });
    } finally {
        savingCard.value = false;
    }
}

/**
 * Check if the status of a participant is editable.
 * @param status The status of the participant.
 * @returns True if the status is editable, false otherwise.
 */
const isEditable = (status: Participant["status"]) => {
    const editableStatuses = ["CREATED", "ACTIVATED", "STARTED"];
    return editableStatuses.includes(status ?? "");
};

const editingRows = ref<Participant[]>([]);

// The row editor's own copy of every open row, by participant id. PrimeVue keeps its copy by row index and drops
// it whenever the table sorts, filters or pages; the inputs then wrote into the row on display, so every keystroke
// in the sorted column re-sorted the table and lost the focus (RDT-484).
const rowDrafts = ref<Record<number, ParticipantDraft>>({});

/** The draft an open row's inputs edit; a throwaway copy while a row is just opening or closing. */
function rowDraft(participant: Participant) {
    return rowDrafts.value[participant.id ?? 0] ?? draftOf(participant);
}

// Rows opening get a draft, rows closing drop theirs.
watch(editingRows, (rows) => {
    rowDrafts.value = Object.fromEntries(
        rows.map((row) => [row.id ?? 0, rowDrafts.value[row.id ?? 0] ?? draftOf(row)])
    );
});

// The edit column has its own buttons instead of PrimeVue's row editor, which moves the focus to its save button
// whenever the table renders again while a row is open, and closes the row before the save has gone through.
const savingRowIds = ref<number[]>([]);

function isRowOpen(participant: Participant) {
    return editingRows.value.some((row) => row.id === participant.id);
}

/** Opens a row's editor with the cursor in the first field the trainer may change. */
async function openRow(participant: Participant, event: Event) {
    const row = (event.currentTarget as HTMLElement | null)?.closest("tr");
    editingRows.value = [...editingRows.value, participant];
    await nextTick();
    const field =
        row?.querySelector<HTMLElement>("input.participant-cell-editor:not(:disabled)") ??
        row?.querySelector<HTMLElement>(".p-datatable-row-editor-save");
    field?.focus();
}

function closeRow(participant: Participant) {
    editingRows.value = editingRows.value.filter((row) => row.id !== participant.id);
}

/** Saves an open row, which stays open with what the trainer typed until the save has gone through. */
async function saveRow(participant: EnrichedParticipant) {
    const draft = rowDrafts.value[participant.id ?? 0];
    if (!participant.id || !draft || isDraftUnchanged(draft, draftOf(participant))) {
        closeRow(participant);
        return;
    }
    if (isEditable(participant.status) && !validateEmail(draft.email)) {
        toast.add({
            severity: "error",
            detail: t("project-configuration.additionalRecipient.error.invalid"),
            life: 3000,
        });
        return;
    }
    const updated = applyDraft(participant, draft);

    const id = participant.id;
    savingRowIds.value = [...savingRowIds.value, id];
    try {
        await ProfileAdministrationAPI.updateParticipant(updated);
        closeRow(participant);
        emit(
            "update:participants",
            props.participants.map((p) => (p.id === updated.id ? updated : p))
        );
    } catch (error) {
        console.error(error);
        toast.add({
            severity: "error",
            detail: t("errors.unexpectedError"),
            life: 3000,
        });
    } finally {
        savingRowIds.value = savingRowIds.value.filter((savingId) => savingId !== id);
    }
}

// Department and role are both optional groupings the trainer can set on a participant, also before
// the questionnaire, which then preselects them (RDT-35). One inline editor serves both columns.
type GroupingField = "departmentId" | "roleId";
const editingGrouping = ref<{ participantId: number; field: GroupingField } | null>(null);
const selectedGroupingId = ref<number | null>(null);

const emailInvalidError = ref(false);

function handleRelease(releasedParticipant: Participant) {
    const updatedParticipants = props.participants?.map((p) =>
        p.id === releasedParticipant.id && p.status === "GENERATED"
            ? { ...p, status: "NOTIFIED" }
            : p
    );

    emit("update:participants", updatedParticipants);
}

async function handleParticipantDeletion(participantId: number) {
    const updatedParticipants = props.participants?.filter(
        (participant: Participant) => participant.id !== participantId
    );
    await updateQuantity();
    emit("update:participants", updatedParticipants);
}

async function handleParticipantUpdate(updatedParticipants: Participant[]) {
    await updateQuantity();
    emit("update:participants", updatedParticipants);
}

async function updateQuantity() {
    quantity.value = await getProductQuantity(props.trainer?.id, props.project?.defaultProduct);
    filterParticipants();
}

function getDepartment(departmentId: number) {
    const department = departmentsStore.getForProject(props.project.id ?? 0).find((d) => d.id === departmentId);
    return department?.name?.toUpperCase();
}

function getRole(roleId: number) {
    const role = roles.value.find((r) => r.id === roleId);
    return role?.name?.toUpperCase();
}

// Function to format score
const formatScore = (score: number) => {
    if (score === null) {
        return "";
    }
    const percentage = (toPercentage(score, 40) * 100).toFixed(2) + "%";
    return `${percentage} (${score})`;
};

function getProfileName(productNo: string) {
    return productNames.value?.[productNo] ?? productNo;
}

/**
 * Maps a participant status to a semantic status-badge class so the badge colour communicates
 * the participant's progress at a glance (green = ready, blue = sent, amber = in progress, ...).
 */
function statusBadgeClass(status: Participant["status"]) {
    switch (status) {
        case "GENERATED":
        case "NOTIFIED":
            return "status-badge status-badge--success";
        case "ACTIVATED":
        case "COMPLETED":
            return "status-badge status-badge--info";
        case "STARTED":
        case "CALCULATED":
        case "FILLED":
            return "status-badge status-badge--progress";
        case "DELETED":
            return "status-badge status-badge--danger";
        default:
            return "status-badge status-badge--neutral";
    }
}

function handleProjectUpdate(project: Project) {
    emit("update:project", project);
}

function validateEmail(email: string) {
    if (isValidEmail(email)) {
        emailInvalidError.value = false;
        return true;
    } else {
        emailInvalidError.value = true;
        return false;
    }
}

function isGroupingEditable(status: Participant["status"] | undefined) {
    return ["CREATED", "ACTIVATED", "STARTED", "GENERATED"].includes(status ?? "");
}

function isEditingGrouping(participant: Participant, field: GroupingField) {
    return editingGrouping.value?.participantId === participant.id && editingGrouping.value?.field === field;
}

function openGroupingEditor(participant: Participant, field: GroupingField) {
    if (!participant.id || !isGroupingEditable(participant.status)) {
        return;
    }

    editingGrouping.value = { participantId: participant.id, field };
    // The backend reports "none" as 0.
    selectedGroupingId.value = participant[field] || null;
}

function closeGroupingEditor() {
    editingGrouping.value = null;
    selectedGroupingId.value = null;
}

async function saveGrouping(participant: Participant, field: GroupingField, id: number | null) {
    if (!participant.id || (participant[field] || null) === id) {
        closeGroupingEditor();
        return;
    }

    try {
        // Omitted, the backend stores none.
        await ProfileAdministrationAPI.updateParticipant({ ...participant, [field]: id ?? undefined });

        const currentParticipants = [...props.participants];
        const participantIndex = currentParticipants.findIndex((p) => p.id === participant.id);

        if (participantIndex === -1) {
            console.error(`Object with id ${participant.id} not found.`);
        } else {
            currentParticipants[participantIndex] = {
                ...currentParticipants[participantIndex],
                [field]: id ?? undefined,
            } as Participant;
            emit("update:participants", currentParticipants);
            filterParticipants();
        }
    } catch {
        toast.add({
            severity: "error",
            detail: t("errors.unexpectedError"),
            life: 3000,
        });
    } finally {
        closeGroupingEditor();
    }
}

watch(
    () => props.participants,
    async () => {
        filterParticipants();
    }
);

// Rows, columns and the layout change what the table needs, besides the dialog's own width.
watch([enrichedParticipants, roles, departments, isCardView], fitTable, { flush: "post" });

const isEditingTable = computed(() => editingRows.value.length > 0 || editingGrouping.value !== null);

// The participants reload while the trainer edits: every 15 s, after each save, several times on opening. New rows
// under an open editor make the table sort again and PrimeVue move the focus to the row's save button, so the
// reload waits until every editor is closed (RDT-484).
const isEditing = computed(() => isEditingTable.value || editingCardId.value !== null);
let reloadPending = false;
watch(isEditing, (editing) => {
    if (!editing && reloadPending) {
        reloadPending = false;
        filterParticipants();
    }
});

/**
 * Pins every column to the width it has right now, or releases them. Opening an editor swaps a cell's
 * text for an input, which would otherwise make the browser lay out all columns anew, and the whole table
 * moved under the trainer's eyes (RDT-456).
 */
// The widths the headers had before pinning (the expander column brings its own).
const unpinnedWidths = new Map<HTMLElement, string>();

function pinColumnWidths(pin: boolean) {
    const root = dt.value?.$el as HTMLElement | undefined;
    if (pin && root) {
        // On screen the widths are zoomed; the inline width is in the table's own, unzoomed pixels.
        const zoom = Number(root.style.zoom || 1);
        root.querySelectorAll<HTMLElement>(".p-datatable-thead > tr > th").forEach((header) => {
            unpinnedWidths.set(header, header.style.width);
            header.style.width = `${header.getBoundingClientRect().width / zoom}px`;
        });
    } else if (!pin) {
        unpinnedWidths.forEach((width, header) => {
            header.style.width = width;
        });
        unpinnedWidths.clear();
    }
}

// "pre": pin while the cells still show text, before the editors render. Once released, fit again: the
// window may have changed while editing.
watch(
    isEditingTable,
    (editing) => {
        pinColumnWidths(editing);
        if (!editing) {
            fitTable();
        }
    },
    { flush: "pre" }
);

onMounted(async () => {
    purchasedProduct.value = await getProduct(props.trainer?.id, props.project.product);
    quantity.value = await getProductQuantity(props.trainer?.id, props.project?.defaultProduct);
    roles.value = await fetchRoles(props.project);
    await departmentsStore.ensureLoaded(props.project);
    productNames.value = await getProductNames();
    expandedRowsMobile.value = props.participants.reduce(
        (acc, participant) => {
            acc[participant.id ?? 0] = true;
            return acc;
        },
        {} as Record<number, boolean>
    );
    await fetchProducts();
    filterParticipants();
    const groupCharts = await ProfileAdministrationAPI.getBaseProductsForGroupChart(props.project?.product ?? "");
    if (groupCharts && groupCharts.length > 0) {
        groupCharts[0].products.products.map((product) => {
            const translatedProduct = products.value.find((p) => p.productNo === product.productNo);
            productOptions.value.push({
                label: translatedProduct?.productName ?? product.productNo,
                value: product.productNo
            });
        });
    }
});
</script>

<template>
    <Toast />
    <h4>{{ t("project-details.participants.heading") }}</h4>
    <!-- Spans the width the participants get, in both layouts; the table fit measures against it -->
    <div ref="widthProbe" class="participants-width-probe" aria-hidden="true" />
    <DataView
        v-if="isCardView"
        :data-key="project.id?.toString()"
        :id="`project-details-participants_dataview-${project.id}`"
        :value="cardParticipants"
        :rows="rows"
        :paginator="showPaginator"
        :pt="{
            header: {
                style: {
                    'margin-bottom': '0 !important',
                },
            },
            content: {
                style: {
                    'border-bottom': '1px solid var(--p-divider-border-color)',
                    'border-top': '1px solid var(--p-divider-border-color)',
                    'background-color': 'white !important',
                },
            },
        }"
    >
        <template #header>
            <div class="flex flex-wrap gap-2 px-0">
                <AddParticipants
                    :disabled="!canAddParticipants"
                    :project="project"
                    :participants="participants"
                    @update:participants="handleParticipantUpdate"
                />
                <ProfileZipDownload
                    v-if="project.recipients !== 'PARTICIPANT'"
                    :participants="participants"
                    :project="project"
                />
                <ProfileReleaseButton
                    v-if="props.participants.some((p) => (p.status === 'GENERATED' || p.status === 'NOTIFIED') && !p.anonymous)"
                    :trainer="trainer" :project="project"
                    @released-participants="handleParticipantUpdate" />
                <EmailAllButton
                    :participants="participants"
                    :project="project"
                    :trainer="trainer"
                    :direct-link="directLink"
                />
                <SendOrRemindAll
                    :trainer="trainer"
                    :participants="participants"
                    :project="project"
                    :product-name="product"
                    :quantity="quantity"
                    @update:participants="handleParticipantUpdate"
                />
                <Button
                    text
                    :id="`project-details-participants_btn_refresh-${project.id}`"
                    icon="pi pi-refresh"
                    @click="emit('reload:participants')"
                    v-tooltip.top="{ value: t('project-details.participants.tooltips.reload'), showDelay: 500 }"
                />
                <div v-if="hasDeletedParticipants" class="flex items-center ml-2">
                    <Checkbox
                        :id="`project-details-participants_checkbox_hide-deleted_mobile-${project.id}`"
                        class="mt-auto mb-auto"
                        v-model="hideDeleted"
                        binary
                        size="small"
                        :aria-label="t('accessibility.checkboxes.hideDeleted')"
                        @change="filterParticipants"
                    />
                    <label
                        :for="`project-details-participants_checkbox_hide-deleted_mobile-${project.id}`"
                        class="small-label ml-1 mt-auto mb-auto"
                    >{{ t("project-details.participants.actionsLabels.hide-delete") }}</label
                    >
                </div>
            </div>
            <div class="w-full mt-2 flex px-0">
                <InputGroup>
                    <IconField>
                        <InputIcon class="pi pi-search" />
                        <InputText
                            :id="`project-details-participants_participant-search-field_mobile-${project.id}`"
                            class="w-full"
                            v-model="filters['global'].value"
                            :placeholder="t('fields.search')"
                        />
                    </IconField>
                    <Button outlined severity="secondary" icon="pi pi-times" @click="resetSearch()" :aria-label="t('accessibility.buttons.clearSearch')" />
                </InputGroup>
            </div>
            <div class="w-full mt-2 flex flex-col px-0">
                <label
                    :for="`project-details-participants_card-sort-field-${project.id}`"
                    class="small-label"
                >{{ t("accessibility.selectors.sortBy") }}</label>
                <InputGroup>
                    <Select
                        :id="`project-details-participants_card-sort-field-${project.id}`"
                        data-cy="card-sort-field"
                        v-model="cardSortField"
                        :options="cardSortOptions"
                        option-label="label"
                        option-value="value"
                        class="w-full"
                    />
                    <Button
                        :id="`project-details-participants_card-sort-order-${project.id}`"
                        data-cy="card-sort-order"
                        class="bg-persolog-blue"
                        :icon="cardSortOrder === 'desc' ? 'pi pi-sort-amount-down' : 'pi pi-sort-amount-up-alt'"
                        @click="toggleCardSortOrder"
                        :aria-label="t('project-overview.sort.order.' + cardSortOrder)"
                    />
                </InputGroup>
            </div>
        </template>
        <template #list="slotProps">
            <div v-for="(participant, index) in slotProps.items" :key="participant.id">
                <div class="participant-card">
                    <!-- Name + status -->
                    <div class="participant-card__top">
                        <span class="participant-card__name">
                            <template v-if="participant.firstName !== '' || participant.name !== ''">
                                {{ participant.firstName }} {{ participant.name }}
                            </template>
                            <template v-else>{{ participant.email }}</template>
                        </span>
                        <Tag
                            :id="`project-details-participants_status-tag-mobile-${participant.id}`"
                            :class="statusBadgeClass(participant.status)"
                            :value="t(`status.${participant.status}`)"
                        />
                    </div>

                    <!-- Inline editor: the card's counterpart of the table's row editor -->
                    <form
                        v-if="editingCardId === participant.id && cardDraft"
                        :id="`project-details-participants_card-editor-${participant.id}`"
                        class="participant-card__editor"
                        data-cy="card-editor"
                        novalidate
                        @submit.prevent="saveCardEditor(participant)"
                    >
                        <div class="participant-card__field">
                            <label :for="`project-details-participants_card-editor-firstname-${participant.id}`">
                                {{ t("project-details.participants.columns.firstName") }}
                            </label>
                            <InputText
                                :id="`project-details-participants_card-editor-firstname-${participant.id}`"
                                data-cy="card-editor-firstname"
                                v-model="cardDraft.firstName"
                                :disabled="!isEditable(participant.status)"
                            />
                        </div>
                        <div class="participant-card__field">
                            <label :for="`project-details-participants_card-editor-name-${participant.id}`">
                                {{ t("project-details.participants.columns.lastName") }}
                            </label>
                            <InputText
                                :id="`project-details-participants_card-editor-name-${participant.id}`"
                                data-cy="card-editor-name"
                                v-model="cardDraft.name"
                                :disabled="!isEditable(participant.status)"
                            />
                        </div>
                        <div class="participant-card__field participant-card__field--wide">
                            <label :for="`project-details-participants_card-editor-email-${participant.id}`">
                                {{ t("project-details.participants.columns.email") }}
                            </label>
                            <InputText
                                :id="`project-details-participants_card-editor-email-${participant.id}`"
                                data-cy="card-editor-email"
                                v-model="cardDraft.email"
                                type="email"
                                :invalid="cardEmailInvalid"
                                :disabled="!isEditable(participant.status)"
                                @input="cardEmailInvalid = false"
                            />
                            <small v-if="cardEmailInvalid" class="text-brand" data-cy="card-editor-email-error">
                                {{ t("project-configuration.additionalRecipient.error.invalid") }}
                            </small>
                        </div>
                        <div v-if="hasProductColumn" class="participant-card__field participant-card__field--wide">
                            <label :for="`project-details-participants_card-editor-product-${participant.id}`">
                                {{ t("project-details.defaultProduct.label-default") }}
                            </label>
                            <Select
                                :id="`project-details-participants_card-editor-product-${participant.id}`"
                                data-cy="card-editor-product"
                                v-model="cardDraft.selectedProduct"
                                filter
                                :options="productOptions"
                                option-label="label"
                                option-value="value"
                                :disabled="!isProductEditable(participant)"
                            />
                        </div>
                        <div v-if="roles.length > 0" class="participant-card__field">
                            <label :for="`project-details-participants_card-editor-role-${participant.id}`">
                                {{ t("project-details.participants.columns.role") }}
                            </label>
                            <Select
                                :id="`project-details-participants_card-editor-role-${participant.id}`"
                                data-cy="card-editor-role"
                                v-model="cardDraft.roleId"
                                :options="roleOptions"
                                option-label="name"
                                option-value="id"
                                :placeholder="t('common.notSelected')"
                                :disabled="!isGroupingEditable(participant.status)"
                            />
                        </div>
                        <div v-if="departments.length > 0" class="participant-card__field">
                            <label :for="`project-details-participants_card-editor-department-${participant.id}`">
                                {{ t("project-details.participants.columns.department") }}
                            </label>
                            <Select
                                :id="`project-details-participants_card-editor-department-${participant.id}`"
                                data-cy="card-editor-department"
                                v-model="cardDraft.departmentId"
                                :options="departmentOptions"
                                option-label="name"
                                option-value="id"
                                :placeholder="t('common.notSelected')"
                                :disabled="!isGroupingEditable(participant.status)"
                            />
                        </div>
                        <div class="participant-card__editor-buttons">
                            <Button
                                :id="`project-details-participants_card-editor-cancel-${participant.id}`"
                                data-cy="card-editor-cancel"
                                type="button"
                                outlined
                                severity="secondary"
                                :label="t('buttons.cancel')"
                                :disabled="savingCard"
                                @click="closeCardEditor"
                            />
                            <Button
                                :id="`project-details-participants_card-editor-save-${participant.id}`"
                                data-cy="card-editor-save"
                                type="submit"
                                class="bg-persolog-blue"
                                :label="t('buttons.save')"
                                :loading="savingCard"
                            />
                        </div>
                    </form>

                    <template v-else>
                        <!-- E-Mail address with copy / send actions -->
                        <div v-if="!project.anonymous && participant.email" class="participant-card__email">
                            <i class="pi pi-at participant-card__email-icon" aria-hidden="true" />
                            <span class="participant-card__email-text">{{ participant.email }}</span>
                            <Button
                                text
                                rounded
                                severity="secondary"
                                icon="pi pi-copy"
                                @click="copyToClipboard(participant.email as string)"
                                v-tooltip.top="{ value: t('project-details.participant.tooltips.copy-email'), showDelay: 500 }"
                                :aria-label="t('project-details.participant.tooltips.copy-email')"
                            />
                            <ParticipantEmailButton
                                :participant="participant as Participant"
                                :project="project"
                                :trainer="trainer"
                                :direct-link="directLink"
                                card
                            />
                        </div>

                        <!-- What the table shows in its own columns: score, product, role, department -->
                        <div
                            v-if="(isBenchmarkProject && participant.score != null) || hasProductColumn || roles.length > 0 || departments.length > 0"
                            class="participant-card__groupings"
                        >
                            <Tag
                                v-if="isBenchmarkProject && participant.score != null"
                                :id="`project-details-participants_score-tag-mobile-${participant.id}`"
                                class="bg-gray-500 text-white"
                                :value="`${t('project-details.participants.columns.score')}: ${formatScore(participant.score)}`"
                            />
                            <Tag
                                v-if="hasProductColumn"
                                :id="`project-details-participants_product-tag-mobile-${participant.id}`"
                                class="bg-gray-500 text-white"
                                :title="t('project-details.defaultProduct.label-default')"
                                :value="getProfileName(participant.selectedProduct)"
                            />
                            <Tag
                                v-if="roles.length > 0"
                                :id="`project-details-participants_role-tag-mobile-${participant.id}`"
                                class="bg-gray-500 text-white"
                                :value="`${t('project-details.participants.columns.role')}: ${participant.roleId ? getRole(participant.roleId) : t('common.notSelected')}`"
                            />
                            <Tag
                                v-if="departments.length > 0"
                                :id="`project-details-participants_department-tag-mobile-${participant.id}`"
                                class="bg-gray-500 text-white"
                                :value="`${t('project-details.participants.columns.department')}: ${participant.departmentId ? getDepartment(participant.departmentId) : t('common.notSelected')}`"
                            />
                        </div>

                        <!-- Contextual primary action(s) + more-actions dropdown -->
                        <div class="participant-card__actions">
                            <div class="participant-card__primary">
                                <BuyButton
                                    v-if="participant.quantity === 0 && participant.status === 'CREATED'"
                                    :trainer="trainer"
                                    :product-name="participant.productName"
                                    :product="participant.selectedProduct"
                                    :shop-link="participant.purchasedProduct?.shopLink"
                                    @update="updateQuantity"
                                    with-classes="bg-persolog-blue"
                                />
                                <QuestionnaireSendButton
                                    :quantity="participant.quantity ?? 0"
                                    :participant="participant"
                                    :trainer="trainer"
                                    :project="project"
                                    @update:participant="handleParticipantUpdate"
                                    with-classes="bg-persolog-blue"
                                />
                                <ProfileViewButton
                                    :participant="participant"
                                    :project="project"
                                    with-classes="bg-persolog-blue"
                                />
                                <ProfileReleaseButton
                                    v-if="['GENERATED', 'NOTIFIED'].includes(participant.status!) && !project.anonymous && !participant.anonymous"
                                    :trainer="trainer"
                                    :project="project"
                                    :participant="participant"
                                    @released-participant="handleRelease"
                                    with-classes="bg-persolog-blue"
                                />
                            </div>
                            <Button
                                v-if="canEditCard(participant)"
                                :id="`project-details-participants_card-edit-btn-${participant.id}`"
                                data-cy="card-edit-button"
                                outlined
                                severity="secondary"
                                icon="pi pi-pencil"
                                @click="openCardEditor(participant)"
                                v-tooltip.top="{ value: t('buttons.edit'), showDelay: 500 }"
                                :aria-label="t('buttons.edit')"
                            />
                            <ParticipantActionsButton
                                :is-direct-link="!canAddParticipants"
                                :participant="participant"
                                :project="project"
                                :trainer="trainer"
                                :profile-quantity="participant.quantity ?? quantity"
                                :product-languages="languages"
                                @update:participant="handleParticipantUpdate"
                                @share:to:participant="handleRelease"
                                @update="updateQuantity"
                                @delete:participant="handleParticipantDeletion"
                                @reload:participants="emit('reload:participants')"
                                @update:collapsed="showInfo(false, participant.id)"
                                @update:project="handleProjectUpdate"
                            />
                        </div>

                        <!-- Expandable details -->
                        <Panel
                            toggleable
                            :collapsed="expandedRowsMobile[participant.id]"
                            @toggle="toggleCollapse(participant.id)"
                            class="participant-card__details"
                            :pt="{
                                root: { style: { border: 'none !important' } },
                                header: { class: 'justify-end px-0 py-1' },
                                content: { class: 'px-0' },
                            }"
                        >
                            <template #header>
                                <span class="participant-card__details-label">
                                    {{ t('project-details.participants.actionsLabels.more-info') }}
                                </span>
                            </template>
                            <Divider class="my-1" />
                            <ProjectDetailsParticipantDetails
                                class="pt-2"
                                :participant="participant as Participant"
                                :trainer="trainer"
                                :project="project"
                                :direct-link="directLink"
                            />
                        </Panel>
                    </template>
                </div>
                <Divider v-if="Number(index) + 1 < participants.length" class="mr-4" />
            </div>
        </template>
        <template #empty>
            <div class="flex justify-center py-4">
                <i v-if="loading" class="pi pi-spin pi-spinner" />
                <span v-else>{{ t("project-details.participants.columns.empty") }}</span>
            </div>
        </template>
    </DataView>
    <DataTable
        v-else
        ref="dt"
        :export-filename="project.name"
        :id="`project-details-participants_table-${project.id}`"
        v-model:expanded-rows="expandedRows"
        v-model:editing-rows="editingRows"
        edit-mode="row"
        paginator
        :rows="rows"
        :value="enrichedParticipants"
        data-key="id"
        v-model:filters="filters"
        :loading="loading"
        :style="{ width: 'auto', 'margin-bottom': showPaginator ? 0 : '1rem' }"
        :global-filter-fields="['name', 'firstName', 'status', 'email']"
        :sort-field="productHasBenchmarkDiagram(project.product) ? 'score' : 'name'"
        :sort-order="productHasBenchmarkDiagram(project.product) ? -1 : 1"
    >
        <template #header>
            <div class="flex justify-between">
                <div class="flex gap-2">
                    <AddParticipants
                        :disabled="!canAddParticipants"
                        :project="project"
                        :participants="participants"
                        @update:participants="handleParticipantUpdate"
                    />
                    <ProfileZipDownload
                        v-if="project.recipients !== 'PARTICIPANT'"
                        :participants="participants"
                        :project="project"
                    />
                    <ProfileReleaseButton
                        v-if="props.participants.some((p) => (p.status === 'GENERATED' || p.status === 'NOTIFIED') && !p.anonymous)"
                        :trainer="trainer" :project="project" @released-participants="handleParticipantUpdate"
                    />
                    <EmailAllButton
                        :participants="participants"
                        :project="project"
                        :trainer="trainer"
                        :direct-link="directLink"
                    />
                    <SendOrRemindAll
                        :trainer="trainer"
                        :participants="participants"
                        :project="project"
                        :product-name="product"
                        :quantity="quantity"
                        @update:participants="handleParticipantUpdate"
                    />

                    <!-- Reload participants & statuses -->
                    <Button
                        text
                        :id="`project-details-participants_btn_refresh-${project.id}`"
                        icon="pi pi-refresh"
                        @click="emit('reload:participants')"
                        v-tooltip.top="{ value: t('project-details.participants.tooltips.reload'), showDelay: 500 }"
                    />
                </div>
                <div class="flex gap-2">
                    <IconField>
                        <InputIcon class="pi pi-search" />
                        <InputText
                            :id="`project-details-participants_participant-search-field-${project.id}`"
                            v-model="filters['global'].value"
                            :placeholder="t('fields.search')"
                        />
                    </IconField>
                    <Button
                        :id="`project-details-participants_btn_reset-search-${project.id}`"
                        outlined
                        severity="secondary"
                        icon="pi pi-times"
                        @click="resetSearch()"
                        :aria-label="t('accessibility.buttons.clearSearch')"
                    />
                </div>
            </div>
        </template>
        <template #empty>
            <span v-if="!loading">{{ t("project-details.participants.columns.empty") }}</span>
        </template>
        <Column expander style="width: 3em"></Column>
        <Column
            v-if="!project.anonymous"
            field="firstName"
            :header="t('project-details.participants.columns.firstName')"
            sortable
        >
            <template #editor="{ data }">
                <InputText v-model="rowDraft(data).firstName" class="participant-cell-editor" :disabled="!isEditable(data.status)" :aria-label="t('accessibility.inputs.firstName')" />
            </template>
        </Column>
        <Column field="name" :header="t('project-details.participants.columns.lastName')" sortable>
            <!-- Last name -->
            <template #editor="{ data }">
                <InputText v-model="rowDraft(data).name" class="participant-cell-editor" :disabled="!isEditable(data.status)" :aria-label="t('accessibility.inputs.lastName')" />
            </template>
        </Column>

        <Column
            v-if="productHasBenchmarkDiagram(project.product)"
            field="score"
            :header="t('project-details.participants.columns.ranking-score')"
            sortable
        >
            <!-- Score -->
            <template #body="{ data }">
                <Tag class="bg-gray-500 text-white" :value="formatScore(data.score)" />
            </template>
        </Column>
        <Column
            v-if="!project.anonymous"
            field="email"
            :header="t('project-details.participants.columns.email')"
            sortable
        >
            <!-- E-Mail Address -->
            <template #editor="{ data }">
                <InputText v-model="rowDraft(data).email" class="participant-cell-editor" :disabled="!isEditable(data.status)" :aria-label="t('accessibility.inputs.email')" @blur="validateEmail(rowDraft(data).email)"/>
                <small v-if="emailInvalidError" class="text-brand">{{ t("project-configuration.additionalRecipient.error.invalid")  }}</small>
            </template>
        </Column>
        <!-- E-Mail actions: pointless without an address (de-anonymized or deleted participants) -->
        <Column :exportable="false" v-if="!project.anonymous" field="email-actions" header="">
            <template #body="{ data }">
                <template v-if="data.email">
                    <Button
                        severity="secondary"
                        class="mr-1"
                        icon="pi pi-copy"
                        data-cy="copy-email-button"
                        @click="copyToClipboard(data.email as string)"
                        v-tooltip="{ value: t('project-details.participant.tooltips.copy-email'), showDelay: 500 }"
                    ></Button>
                    <ParticipantEmailButton
                        :participant="data as Participant"
                        :project="project"
                        :trainer="trainer"
                        :direct-link="directLink"
                    />
                </template>
            </template>
        </Column>
        <Column
            :exportable="false"
            v-if="project && project.defaultProduct && project.product !== project.defaultProduct"
            field="selectedProduct"
            :header="t('project-details.defaultProduct.label-default')"
        >
            <template #editor="{ data }">
                <Select
                    filter
                    class="participant-cell-editor"
                    :disabled="!isProductEditable(data)"
                    v-model="rowDraft(data).selectedProduct"
                    option-label="label"
                    option-value="value"
                    :options="productOptions"
                    :placeholder="t('project-details.defaultProduct.label-default')"
                >
                    <template #option="slotProps">
                        <span>
                            {{ slotProps.option.label }}
                            ({{ slotProps.option.value }})
                        </span>
                    </template>
                </Select>
            </template>
            <template #body="{ data }">
                {{ getProfileName(data.selectedProduct) }}
            </template>
        </Column>
        <!-- Roles -->
        <Column
            v-if="roles.length > 0"
            field="roleName"
            :header="t('project-details.participants.columns.role')"
            sortable
        >
            <template #editor="{ data }">
                <Select
                    :id="`project-details-participants_role-row-editor-${data.email}`"
                    v-model="rowDraft(data).roleId"
                    :options="roleOptions"
                    option-label="name"
                    option-value="id"
                    class="w-full participant-cell-editor"
                    :disabled="!isGroupingEditable(data.status)"
                    :placeholder="t('project-details.participants.columns.role')"
                    :aria-label="t('project-details.participants.columns.role')"
                />
            </template>
            <template #body="{ data }">
                <div v-if="isEditingGrouping(data as Participant, 'roleId')" class="grouping-select-container">
                    <Select
                        :id="`project-details-participants_role-select-${data.email}`"
                        v-model="selectedGroupingId"
                        :options="roleOptions"
                        option-label="name"
                        option-value="id"
                        class="w-full participant-cell-editor"
                        :aria-label="t('project-details.participants.columns.role')"
                        @change="saveGrouping(data as Participant, 'roleId', $event.value)"
                        @hide="closeGroupingEditor"
                    />
                </div>
                <button
                    v-else
                    type="button"
                    class="grouping-tag-button role-tag-button"
                    :id="`project-details-participants_role-tag-edit-btn-${data.id}`"
                    :disabled="!isGroupingEditable(data.status)"
                    @click="openGroupingEditor(data as Participant, 'roleId')"
                >
                    <Tag
                        class="bg-gray-500 text-white"
                        :value="data.roleId ? getRole(data.roleId) : t('common.notSelected')"
                    />
                </button>
            </template>
        </Column>
        <!-- Departments -->
        <Column
            v-if="departments.length > 0"
            field="departmentName"
            :header="t('project-details.participants.columns.department')"
            sortable
        >
            <template #editor="{ data }">
                <Select
                    :id="`project-details-participants_department-row-editor-${data.email}`"
                    v-model="rowDraft(data).departmentId"
                    :options="departmentOptions"
                    option-label="name"
                    option-value="id"
                    class="w-full participant-cell-editor"
                    :disabled="!isGroupingEditable(data.status)"
                    :placeholder="t('project-details.participants.columns.department')"
                    :aria-label="t('project-details.participants.columns.department')"
                />
            </template>
            <template #body="{ data }">
                <div v-if="isEditingGrouping(data as Participant, 'departmentId')" class="grouping-select-container">
                    <Select
                        :id="`project-details-participants_department-select-${data.email}`"
                        v-model="selectedGroupingId"
                        :options="departmentOptions"
                        option-label="name"
                        option-value="id"
                        class="w-full participant-cell-editor"
                        :aria-label="t('project-details.participants.columns.department')"
                        @change="saveGrouping(data as Participant, 'departmentId', $event.value)"
                        @hide="closeGroupingEditor"
                    />
                </div>
                <button
                    v-else
                    type="button"
                    class="grouping-tag-button department-tag-button"
                    :id="`project-details-participants_department-tag-edit-btn-${data.id}`"
                    :disabled="!isGroupingEditable(data.status)"
                    @click="openGroupingEditor(data as Participant, 'departmentId')"
                >
                    <Tag
                        class="bg-gray-500 text-white"
                        :value="data.departmentId ? getDepartment(data.departmentId) : t('common.notSelected')"
                    />
                </button>
            </template>
        </Column>
        <!-- Status -->
        <Column field="status" :header="t('project-details.participants.columns.status')" sortable>
            <template #body="{ data }">
                <Tag
                    :id="`project-details-participants_status-tag-${data.id}`"
                    :class="statusBadgeClass(data.status)"
                    :value="t(`status.${data.status}`)"
                />
            </template>
        </Column>
        <Column :exportable="false" field="download" :header="t('project-details.participants.columns.actions')">
            <template #body="slotProps">
                <div class="flex gap-2 justify-end">
                    <BuyButton
                        v-if="slotProps.data.quantity === 0 && slotProps.data.status === 'CREATED'"
                        :trainer="trainer"
                        :product-name="slotProps.data.productName"
                        :product="slotProps.data.selectedProduct"
                        :shop-link="slotProps.data.purchasedProduct?.shopLink"
                        :with-label="false"
                        @update="updateQuantity"
                        with-classes="bg-persolog-blue"
                    />
                    <QuestionnaireSendButton
                        :quantity="slotProps.data.quantity"
                        :participant="slotProps.data"
                        :trainer="trainer"
                        :project="project"
                        @update:participant="handleParticipantUpdate"
                        with-classes="bg-persolog-blue"
                    />
                    <ProfileViewButton :participant="slotProps.data" :project="project" with-classes="bg-persolog-blue" />
                    <ProfileReleaseButton
                        v-if="['GENERATED', 'NOTIFIED'].includes(slotProps.data.status!) && !props.project.anonymous && !slotProps.data.anonymous"
                        :trainer="trainer"
                        :project="project"
                        :participant="slotProps.data"
                        @released-participant="handleRelease"
                        with-classes="bg-persolog-blue"
                    />
                    <ParticipantActionsButton
                        :is-direct-link="!canAddParticipants"
                        :participant="slotProps.data"
                        :project="project"
                        :trainer="trainer"
                        :profile-quantity="slotProps.data.quantity"
                        :product-languages="languages"
                        @update:participant="handleParticipantUpdate"
                        @share:to:participant="handleRelease"
                        @update="updateQuantity"
                        @delete:participant="handleParticipantDeletion"
                        @reload:participants="emit('reload:participants')"
                        @update:collapsed="showInfo(true, slotProps.data.id)"
                        @update:project="handleProjectUpdate"
                    />
                </div>
            </template>
        </Column>
        <Column
            :exportable="false"
            v-if="!project.anonymous"
            :header="t('project-details.participants.columns.edit')"
            header-style="text-align:center"
            body-style="text-align:center"
        >
            <!-- PrimeVue's row editor classes, so the theme and the e2e selectors keep working -->
            <template #body="{ data }">
                <template v-if="isRowOpen(data as Participant)">
                    <Button
                        class="p-datatable-row-editor-save"
                        icon="pi pi-check"
                        text
                        rounded
                        severity="secondary"
                        :loading="savingRowIds.includes(data.id)"
                        :aria-label="t('buttons.save')"
                        @click="saveRow(data as EnrichedParticipant)"
                    />
                    <Button
                        class="p-datatable-row-editor-cancel"
                        icon="pi pi-times"
                        text
                        rounded
                        severity="secondary"
                        :disabled="savingRowIds.includes(data.id)"
                        :aria-label="t('buttons.cancel')"
                        @click="closeRow(data as Participant)"
                    />
                </template>
                <Button
                    v-else
                    class="p-datatable-row-editor-init"
                    icon="pi pi-pencil"
                    text
                    rounded
                    severity="secondary"
                    :aria-label="t('buttons.edit')"
                    @click="openRow(data as Participant, $event)"
                />
            </template>
        </Column>
        <template #expansion="slotProps">
            <ProjectDetailsParticipantDetails
                :participant="slotProps.data as Participant"
                :trainer="trainer"
                :project="project"
                :direct-link="directLink"
            />
        </template>
        <template #paginatorstart>
            <div
                v-if="hasDeletedParticipants"
                class="flex items-center ml-2 mr-auto mt-auto mb-auto"
            >
                <Checkbox
                    :id="`project-details-participants_checkbox_hide-deleted-${project.id}`"
                    class="mt-auto mb-auto"
                    v-model="hideDeleted"
                    binary
                    size="small"
                    :aria-label="t('accessibility.checkboxes.hideDeleted')"
                    @change="filterParticipants"
                />
                <label
                    :for="`project-details-participants_checkbox_hide-deleted-${project.id}`"
                    class="small-label ml-1 mt-auto mb-auto"
                >{{ t("project-details.participants.actionsLabels.hide-delete") }}</label
                >
            </div>
            &nbsp;
        </template>
        <template #paginatorend>
            <Button
                icon="pi pi-download"
                text
                :label="t('buttons.export')"
                size="small"
                @click="exportCSV()"
                :disabled="participants.length == 0"
            />
        </template>
    </DataTable>
</template>

<style>
/*
 * PrimeVue's DataView wraps every item in an unstyled div. Without a max-width
 * that wrapper is sized by the item's min-content, and the participant card's
 * e-mail row floors it well above the dialog width -- so on narrow viewports
 * the cards spilled past the dialog edge. Constraining the wrapper lets the
 * e-mail's existing ellipsis do its job instead.
 *
 * Unscoped and :has()-anchored on purpose: the dialog is teleported to <body>,
 * so it carries no component scope attribute and :deep() can never match it,
 * while :has() keeps this off every other DataView in the app.
 */
.p-dataview-content > *:has(> .participant-card) {
    max-width: 100%;
}

.p-dataview .p-dataview-header {
    padding-left: 0 !important;
    padding-right: 0 !important;
}

.small-label {
    font-size: 0.85rem;
}

#project-details-participants_table .p-datatable-thead > tr > th,
[id^="project-details-participants_table-"] .p-datatable-thead > tr > th {
    padding-top: 0.1rem;
    padding-bottom: 0.1rem;
}

#project-details-participants_table .p-datatable-column-header-content .p-icon,
[id^="project-details-participants_table-"] .p-datatable-column-header-content .p-icon {
    width: 0.8rem;
    height: 0.8rem;
}

.grouping-tag-button {
    border: none;
    background: transparent;
    padding: 0;
    cursor: pointer;
}

.grouping-tag-button:disabled {
    cursor: default;
}

.grouping-select-container {
    width: 100%;
}

/*
 * Editors inside table cells take the width the column already has and nothing else. Left alone, an input
 * asks for about 20 characters and a select for its whole label: that widened the table by ~265px and, on
 * wide screens, handed the spare room to other columns, so every column moved and every row grew as soon
 * as a row was edited (RDT-456). Width 0 keeps them out of the column sizing; min-width 100% still fills
 * the cell once the columns are laid out.
 */
.participant-cell-editor {
    width: 0;
    min-width: 100%;
}

.participant-cell-editor .p-select-label {
    width: 0;
}

/* ---- Mobile participant card ---- */
.participant-card {
    padding: 0.75rem 0.25rem 0.25rem 0.5rem;
}

.participant-card__top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.5rem;
}

.participant-card__name {
    font-weight: 700;
    font-size: 0.95rem;
    line-height: 1.3;
    word-break: break-word;
}

.participant-card__email {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-top: 0.5rem;
}

.participant-card__email-icon {
    font-size: 0.85rem;
    color: var(--p-text-muted-color);
}

.participant-card__email-text {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.85rem;
    color: var(--p-text-muted-color);
}

.participant-card__actions {
    display: flex;
    align-items: stretch;
    gap: 0.5rem;
    margin-top: 0.75rem;
}

.participant-card__primary {
    display: flex;
    flex: 1 1 auto;
    gap: 0.5rem;
}

.participant-card__primary > * {
    flex: 1 1 auto;
}

.participant-card__primary .p-button {
    width: 100%;
}

.participant-card__groupings {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
    margin-top: 0.5rem;
}

/* Inline editor: one column on phones, two on tablets (the card list runs up to 991px) */
.participant-card__editor {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 0.75rem;
    margin-top: 0.75rem;
}

@media screen and (min-width: 576px) {
    .participant-card__editor {
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    }

    .participant-card__field--wide,
    .participant-card__editor-buttons {
        grid-column: 1 / -1;
    }
}

.participant-card__field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.participant-card__field label {
    font-size: 0.8rem;
    color: var(--p-text-muted-color);
}

.participant-card__editor-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
}

.participant-card__details-label {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--p-secondary-500);
}

/* Status badges: semantic colours so progress reads at a glance */
.status-badge {
    font-weight: 600;
    font-size: 0.7rem;
    letter-spacing: 0.04em;
}

.status-badge.status-badge--success {
    background-color: #eaf3de !important;
    color: #27500a !important;
}

.status-badge.status-badge--info {
    background-color: #e6f1fb !important;
    color: #0c447c !important;
}

.status-badge.status-badge--progress {
    background-color: #faeeda !important;
    color: #854f0b !important;
}

.status-badge.status-badge--danger {
    background-color: #fcebeb !important;
    color: #791f1f !important;
}

.status-badge.status-badge--neutral {
    background-color: #f1efe8 !important;
    color: #444441 !important;
}
</style>
